import { type VFC, useMemo } from "react";
import { useNavigate } from "react-router";

import { ActionButton, ActionButtonIntent, Card, type GridCard, HStack } from "widgets";

import { getBlastTargetLinkPath, getHitAccessionVersion } from "../../app-utils/blast.js";
import { GREY_COLOR } from "../../settings/colors-settings.js";
import {
  HIT_MISSING_ACCESSION,
  HIT_MISSING_EXT_INDEX,
  HIT_MISSING_INDEX_NOT_IN_SYNC,
  SEQUENCE_TYPE,
} from "../../settings/settings.js";
import { getLink } from "../core/grid/cells/HitAccessionCell.js";
import { type IRenderableHit } from "./AlnPaginationModel.js";
import { SensorHsp } from "./SensorHsp.js";

export enum SequenceType {
  DNA,
  AMINOACID,
}

export interface ISensorAlnInfoCardProps extends GridCard {
  type: SequenceType;
  hit: IRenderableHit;
  queryId: number;
  jobId: number;
}

/**
 * This component creates a card that displays one BLAST hit with one or more HSP alignments
 *
 * The hierarchy is as follows ('+' denotes one or more instances contained)
 *
 * SensorAlnInfoCard.tsx
 *  -> (Hsp.tsx)+
 *      -> PairwiseAlignment
 *          -> (ColoredSequence)+
 */
const SensorAlnInfoCard: VFC<ISensorAlnInfoCardProps> = (props) => {
  const navigate = useNavigate();
  const hasGenomeView = useMemo(
    () => props.hit.target?.type?.name === SEQUENCE_TYPE.GENOMIC && props.hit.target?.organism?.id !== 0,
    [props.hit],
  );

  const accessionVersion = useMemo(() => {
    const accessionValue = getHitAccessionVersion(props.hit);
    return !props.hit.target || accessionValue === HIT_MISSING_ACCESSION ? (
      <span
        style={{ fontStyle: "italic" }}
        title={props.hit.target?.isExternal ? HIT_MISSING_EXT_INDEX : HIT_MISSING_INDEX_NOT_IN_SYNC}
      >
        {HIT_MISSING_ACCESSION.toUpperCase()}
      </span>
    ) : (
      getLink(props.hit, accessionValue, undefined, props.jobId, props.queryId)
    );
  }, [props.hit, props.jobId, props.queryId]);

  //  Warning: no summary available for the subject sequence of this alignment
  //  The subject sequence of this hit has no counterpart in the database
  const description = useMemo(
    () => (!props.hit.target ? HIT_MISSING_INDEX_NOT_IN_SYNC : (props.hit.target.description ?? "")),
    [props.hit],
  );

  return (
    <Card
      title={
        <HStack style={{ minWidth: 0 }}>
          <span style={{ color: GREY_COLOR.BATTLESHIP_GREY }}>{props.hit.rank}</span>
          {accessionVersion}:
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {description}
          </div>
        </HStack>
      }
      subtitle={
        <HStack spacing={10}>
          <div>
            Length:{" "}
            <strong>{props.hit.target && !props.hit.target.isExternal ? props.hit.target.length : "N/A"}</strong>
          </div>
          <div>
            Number of matches: <strong>{props.hit.totalHspCount}</strong>
          </div>
          {props.hit.partialHspList && (
            <div>
              Showing HSPs:{" "}
              <strong>
                {props.hit.first}-{props.hit.last}
              </strong>
            </div>
          )}
        </HStack>
      }
      actions={[
        hasGenomeView && (
          <ActionButton
            key={"sensor-aln-action-1"}
            onPress={() =>
              navigate(`/job/${props.jobId}/results/${props.queryId}/hitdetails/${props.hit.rank}/genomeview`)
            }
            tooltip={"Show all HSPs of this hit in Genome View"}
          >
            Genome View
          </ActionButton>
        ),
        <ActionButton
          key={"sensor-aln-action-2"}
          onPress={() => navigate(getBlastTargetLinkPath(props.hit, props.jobId, props.queryId))}
        >
          Summary
        </ActionButton>,
        <ActionButton
          key={"sensor-aln-action-3"}
          onPress={() => navigate(`/job/${props.jobId}/results/${props.queryId}/hits/${props.hit.rank}`)}
          intent={ActionButtonIntent.Outlined}
        >
          Back to hits
        </ActionButton>,
      ]}
    >
      {props.hit.hsps?.map(
        (hsp) =>
          hsp && (
            <SensorHsp
              hit={props.hit}
              type={props.type}
              hsp={hsp}
              jobId={props.jobId}
              queryId={props.queryId}
              key={`hsp-${props.hit.rank}-${hsp.rank}`}
            />
          ),
      )}
    </Card>
  );
};

export { SensorAlnInfoCard };
